import { Controller } from "@hotwired/stimulus"
import {
  applyFloatingPanel,
  placeBesideBadge,
  placeFloatingPanel,
} from "../lib/floating_panel"

// The track picker, shared by season setup's schedule preview and a round's
// inline track field.
//
//   data-track-picker-mode-value="input" | "host"
//
// "input" owns a hidden input: picking writes it and fires `change`, so a host
// like inline-field commits without knowing this controller exists. "host"
// writes nothing and announces `track-picker:select` instead, for a caller
// that already keeps its own state — setup's preview holds per-row overrides
// and re-renders the table itself.
//
// The panel is `position: fixed`, which escapes the card or drawer it opens
// in without being re-parented: moving it would take it out of this
// controller's scope and unbind every action inside it. Placement, the flip and
// the viewport clamps come from lib/floating_panel, shared with the combobox so
// the two can't behave differently.
//
// The panel takes the width of whatever opened it, so a roomy surface gets a
// roomy list instead of a 280px column with truncated track names. Bounded at
// both ends: narrow enough to read in a collapsed drawer, wide enough that a
// full-width row does not stretch the list across the screen.
const MIN_WIDTH = 280
const MAX_WIDTH = 420

export default class extends Controller {
  static targets = [
    "panel",
    "search",
    "option",
    "hoverCard",
    "input",
    "count",
    "scroller",
  ]
  static values = {
    mode: { type: String, default: "input" },
    autoopen: { type: Boolean, default: false },
    // Selector for the control the panel hangs off, when the host knows it and
    // this markup doesn't — the planner's track trigger lives outside the
    // lazily-fetched frame this controller arrives in.
    anchor: { type: String, default: "" },
  }

  connect() {
    // Bound only while the panel is open, and only from the next task: the very
    // click that opens it is still travelling to document, and the opener is not
    // always this controller's own trigger — the round's inline field opens the
    // picker by revealing it, so exempting `track-picker#open` is not enough.
    this._onDocumentClick = (e) => {
      if (this.panelTarget.contains(e.target)) return
      this.close()
    }
    this._onKeydown = (e) => {
      if (this._closed) return
      if (e.key === "Escape") {
        e.stopPropagation()
        this.close()
      } else if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "Enter"
      ) {
        this._navigate(e)
      }
    }
    document.addEventListener("keydown", this._onKeydown)

    // A `fixed` panel is anchored to a trigger that scrolls: without following
    // it, scrolling the drawer's round list leaves the list floating over an
    // unrelated row. `true` = capture, because scroll doesn't bubble.
    this._reposition = () => {
      if (this._closed) return
      if (!this._anchor?.isConnected) return this.close()
      this._applyPlacement(this._anchor)
    }
    window.addEventListener("resize", this._reposition)
    window.addEventListener("scroll", this._reposition, true)

    // A host that reveals the picker rather than clicking a trigger inside it
    // (the round's inline field) asks for it this way, and says what to hang
    // it off.
    this._onOpenRequest = (e) =>
      this.openAt(e.detail?.anchor || this._defaultAnchor())
    this.element.addEventListener(
      "track-picker:openRequest",
      this._onOpenRequest,
    )

    // The field was already clicked to get here — the picker is lazily fetched,
    // so the click that asked for it happened before this markup existed.
    if (this.autoopenValue)
      requestAnimationFrame(() => this.openAt(this._defaultAnchor()))
  }

  // The value the picker stands in for, when there is one: anchoring to the
  // panel's own wrapper would place it against a zero-height box.
  _defaultAnchor() {
    if (this.anchorValue) {
      const named = document.querySelector(this.anchorValue)
      if (named) return named
    }
    const field = this.element.closest("[data-inline-field-target='field']")
    return (
      field?.querySelector("[data-inline-field-target='display']") ||
      this.element.parentElement ||
      this.element
    )
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("resize", this._reposition)
    window.removeEventListener("scroll", this._reposition, true)
    this.element.removeEventListener(
      "track-picker:openRequest",
      this._onOpenRequest,
    )
  }

  get _closed() {
    return this.panelTarget.classList.contains("hidden")
  }

  _watchOutside() {
    clearTimeout(this._watchTimer)
    this._watchTimer = setTimeout(
      () => document.addEventListener("click", this._onDocumentClick),
      0,
    )
  }

  open(event) {
    // Remembered rather than looked up on pick: by then the row may have been
    // re-rendered and the element that opened this is gone.
    this.openerKey = event.currentTarget.dataset.pickerKey ?? null
    this.openAt(event.currentTarget)
  }

  openAt(anchor) {
    this._anchor = anchor
    this.panelTarget.classList.remove("hidden")
    this._clearCursor()
    this._applyPlacement(anchor)

    this.searchTarget.value = ""
    this.filter()
    this._revealSelected()
    this._watchOutside()
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  _applyPlacement(anchor) {
    applyFloatingPanel(
      this.panelTarget,
      placeFloatingPanel(anchor, { minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH }),
    )
  }

  close() {
    clearTimeout(this._watchTimer)
    document.removeEventListener("click", this._onDocumentClick)
    this.panelTarget.classList.add("hidden")
    this._anchor = null
    this._clearCursor()
    this.hideCard()
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    // Typing clears the cursor: the row it was on may not be in the list any
    // more, and a stale cursor is a promise about Enter that the list can't keep.
    this._clearCursor()
    let shown = 0
    this.optionTargets.forEach((o) => {
      const match = q === "" || o.dataset.search.includes(q)
      o.classList.toggle("hidden", !match)
      if (match) shown += 1
    })
    if (this.hasCountTarget) {
      this.countTarget.textContent = `${shown} of ${this.optionTargets.length}`
    }
  }

  // ───── keyboard ─────

  // The cursor starts from the currently-set track, not from row 0: ↓ in a list
  // whose 9th track is selected should offer the 10th. Wraps at both ends;
  // Enter with no cursor takes the top match, which is what the filter narrowed
  // the list down to.
  _navigate(event) {
    const visible = this.optionTargets.filter(
      (o) => !o.classList.contains("hidden"),
    )
    if (!visible.length) return
    event.preventDefault()

    const cursor = visible.findIndex((o) => o.dataset.active === "true")
    if (event.key === "Enter") {
      ;(cursor >= 0 ? visible[cursor] : visible[0]).click()
      return
    }

    const from =
      cursor >= 0
        ? cursor
        : visible.findIndex((o) => o.getAttribute("aria-selected") === "true")
    const step = event.key === "ArrowDown" ? 1 : -1
    const next =
      from < 0
        ? step > 0
          ? 0
          : visible.length - 1
        : (from + step + visible.length) % visible.length

    this._clearCursor()
    visible[next].dataset.active = "true"
    this._reveal(visible[next])
  }

  _clearCursor() {
    this.optionTargets.forEach((o) => delete o.dataset.active)
  }

  _revealSelected() {
    const selected = this.optionTargets.find(
      (o) => o.getAttribute("aria-selected") === "true",
    )
    if (selected) this._reveal(selected)
  }

  // Scrolls the panel's own list, never `scrollIntoView` — that walks up every
  // scrollable ancestor and moves the drawer out from under the trigger.
  _reveal(option) {
    if (!this.hasScrollerTarget) return
    const list = this.scrollerTarget
    const top = option.offsetTop - list.offsetTop
    const bottom = top + option.offsetHeight
    if (top < list.scrollTop) list.scrollTop = top
    else if (bottom > list.scrollTop + list.clientHeight)
      list.scrollTop = bottom - list.clientHeight
  }

  pick(event) {
    this._commit(event.currentTarget.dataset.trackId)
  }

  clear() {
    this._commit("")
  }

  _commit(trackId) {
    if (this.modeValue === "host") {
      this.dispatch("select", { detail: { trackId, key: this.openerKey } })
    } else if (this.hasInputTarget) {
      this.inputTarget.value = trackId
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.close()
  }

  // ───── the add-on card ─────

  showCard(event) {
    const html = event.currentTarget.dataset.card
    if (!html || !this.hasHoverCardTarget) return

    const card = this.hoverCardTarget
    card.innerHTML = html
    card.classList.remove("hidden")

    // Anchored to the DLC badge, not the row: a row spans the whole panel, so
    // a row-anchored card lands at the panel's edge, nowhere near what was
    // hovered. It flips to the badge's left when there is no room right, and is
    // clamped against its own *measured* height — the card's copy is variable,
    // so a guessed height puts a tall one off the bottom of the screen.
    const badge = event.currentTarget.querySelector("[data-dlc-badge]")
    const cr = card.getBoundingClientRect()
    const at = placeBesideBadge(badge || event.currentTarget, {
      width: cr.width,
      height: cr.height,
    })
    card.style.left = `${at.left}px`
    card.style.top = `${at.top}px`

    // Same correction the panel gets: `fixed` may be resolving against an
    // ancestor rather than the viewport, so verify where it actually landed.
    const placed = card.getBoundingClientRect()
    const dx = at.left - placed.left
    const dy = at.top - placed.top
    if (Math.abs(dx) > 0.5) card.style.left = `${at.left + dx}px`
    if (Math.abs(dy) > 0.5) card.style.top = `${at.top + dy}px`
  }

  hideCard() {
    this.hoverCardTarget?.classList.add("hidden")
  }
}
